import {
    Button,
    Card,
    FluentProvider,
    MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle,
    SelectTabData,
    SelectTabEvent,
    Spinner,
    tokens,
} from "@fluentui/react-components";
import {useEffect, useState} from "react";
import {app} from "@microsoft/teams-js";
import {useTeamsUserCredential} from "@microsoft/teamsfx-react";
import {TeamsFxContext} from "./TeamsFxContext";
import "react"
import {AudiencesTab} from "./components/audiences/AudiencesTab.tsx";
import {useAtom, useSetAtom} from "jotai";
import {appIdAtom, errorAtom, selectedTabAtom, teamsUserCredentialAtom} from "./store.ts";
import {TabNavigation} from "./components/TabNavigation.tsx";
import {CampaignsTab} from "./components/campaigns/CampaignsTab.tsx";
import {SeriesTab} from "./components/series/SeriesTab.tsx";
import {HomeTab} from "./components/home/HomeTab.tsx";
import {SecuredWrapper} from "./components/wrapper/SecuredWrapper.tsx";
import {useTranslation} from "react-i18next";
import {ReportTab} from "./components/report/ReportTab.tsx";
import {Dismiss16Regular} from "@fluentui/react-icons";

const config = {
    initiateLoginEndpoint: "/auth-start.html",
    clientId: import.meta.env.VITE_CLIENT_ID,
};

const CURRENT_VERSION = __APP_VERSION__;

export default function App() {
    const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom)
    const setAppId = useSetAtom(appIdAtom)
    const [errorMessage, setErrorMessage] = useAtom(errorAtom)
    const setTeamsUserCredential = useSetAtom(teamsUserCredentialAtom)
    const [initialized, setInitialized] = useState<boolean>(false);
    const [newVersionAvailable, setNewVersionAvailable] = useState<string | null>(null);
    const { t, i18n } = useTranslation();

    const {loading, theme, themeString, teamsUserCredential, inTeams} = useTeamsUserCredential({
        initiateLoginEndpoint: config.initiateLoginEndpoint!,
        clientId: config.clientId!,
    });

    const refreshApp = () => {
        // Force reload with a cache-busting query string
        window.location.href = `${window.location.origin}${window.location.pathname}?_=${Date.now()}`;
    };

    // Function to check the app version
    const checkAppVersion = async () => {
        try {
            // Fetch the version.json file
            const response = await fetch("/version.json", { cache: "no-store" });
            if (!response.ok) {
                console.error("Failed to fetch version.json");
                return;
            }
    
            const { version: latestVersion } = await response.json();
    
            if (latestVersion !== CURRENT_VERSION) {
                setNewVersionAvailable(latestVersion);
            } else {
                console.log(`App is up-to-date: ${CURRENT_VERSION}`);
            }
        } catch (error) {
            console.error("Error checking app version:", error);
        }
    };
    
    useEffect(() => {
        // Initial version check
        checkAppVersion();
        
        // Set up periodic version check every 5 minutes (300000 ms)
        const versionCheckInterval = setInterval(() => {
            console.log("Checking for new version...");
            checkAppVersion();
        }, 300000); // 5 minutes
        
        // Clean up interval on component unmount
        return () => {
            clearInterval(versionCheckInterval);
        };
    }, []);


    useEffect(() => {
        if (inTeams !== undefined && !inTeams) {
            setErrorMessage("This app can only be used inside Teams")
        }
    }, [inTeams]);


    useEffect(() => {
        if (teamsUserCredential && inTeams) {
            app.initialize()
                .then(_ => app.getContext())
                .then(async context => {
                    setAppId(context?.app.appId?.toString());
                    await i18n.changeLanguage(context.app.locale?.split('-')[0] || 'en')
                })
                .then(_ => setTeamsUserCredential(teamsUserCredential))
                .then(_ => app.notifySuccess())
                .then(_ => setInitialized(true))
                .catch(_ => setErrorMessage("Failed to initialize Teams app. Please try again later."))
        }
    }, [teamsUserCredential, inTeams]);

    const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
        setSelectedTab(data.value as any);
    };

    const renderTabContent = () => {
        switch (selectedTab) {
            case "home":
                return <HomeTab/>;
            case "groups":
                return <SecuredWrapper initialized={initialized}><AudiencesTab/></SecuredWrapper>;
            case "availableModules":
                return <SecuredWrapper initialized={initialized}><SeriesTab/></SecuredWrapper>;
            case "activeModules":
                return <SecuredWrapper initialized={initialized}><CampaignsTab/></SecuredWrapper>;
            case "reports":
                return <SecuredWrapper initialized={initialized}><ReportTab/></SecuredWrapper>;
            default:
                return <HomeTab/>;
        }
    };

    const renderAllowed = !loading && inTeams;


    return (
        <TeamsFxContext.Provider value={{theme, themeString, teamsUserCredential}}>
            <FluentProvider theme={theme} style={{background: tokens.colorNeutralBackground3, minHeight: '100vh'}}>
                {newVersionAvailable && (
                    <MessageBar intent="info" style={{width: '100%', padding: '10px', position: 'sticky', top: 0, zIndex: 1000}}>
                        <MessageBarBody>
                            <MessageBarTitle>{t("New version available")}</MessageBarTitle>
                            {t("A new version of this application is available.")}
                        </MessageBarBody>
                        <MessageBarActions>
                            <Button appearance="primary" onClick={refreshApp}>
                                {t("Refresh")}
                            </Button>
                            <Button
                                aria-label="dismiss"
                                onClick={() => setNewVersionAvailable(null)}
                                appearance="transparent"
                                icon={<Dismiss16Regular />}
                            />
                        </MessageBarActions>
                    </MessageBar>
                )}
                <div style={{padding: '20px'}}>
                    <TabNavigation selectedValue={selectedTab} onTabSelect={onTabSelect}/>
                    {errorMessage && (
                        <MessageBar  intent="error" style={{marginTop: '20px', width: '100%', padding: '10px'}}>
                            <MessageBarBody>
                                <MessageBarTitle>Error</MessageBarTitle>
                                {errorMessage}
                            </MessageBarBody>
                            <MessageBarActions
                                containerAction={
                                    <Button
                                        aria-label="dismiss"
                                        onClick={() => setErrorMessage(null)}
                                        appearance="transparent"
                                        icon={<Dismiss16Regular />}
                                    />
                                }>
                            </MessageBarActions>
                        </MessageBar>
                    )}

                    <div style={{marginTop: '20px'}}>
                        {!renderAllowed && <Card><Spinner label={t("text.initializing")}></Spinner></Card>}
                        {renderAllowed && renderTabContent()}
                    </div>
                </div>
            </FluentProvider>
        </TeamsFxContext.Provider>
    );
}
